/**
 * What a body is **doing**, as one code, for every body on the floor.
 *
 * ## The gap this closes
 *
 * A `PlayerState` carried `walking` and `dead`; a `MonsterState` carried
 * `walking`. That is the whole animation vocabulary the wire had, and it means a
 * melee monster standing at its reach and swinging is replicated as a monster
 * standing still — *"the one frame a player most needs to see coming"*
 * (`TODO.md` #260). No amount of art in `apps/web` can draw a state the wire
 * cannot express, so this is the half that has to exist first.
 *
 * ## One `uint8` rather than a fistful of booleans
 *
 * `TODO.md` #260 sets out three shapes and this is the second of them: a `uint8`
 * activity code replacing nothing, beside the two flags that were already here.
 * The other two were rejected on their own terms:
 *
 *   - **A `boolean attacking`** costs the same two bytes a transition and says
 *     only one thing. Every state after it — hurt, casting, blocked — would want
 *     a flag of its own, four flags is four field indices on every body, and
 *     *"a body that is both hurt and striking"* becomes a question every client
 *     answers differently. One field is one answer.
 *   - **A discrete message per swing** costs nothing per tick and leaves a
 *     joining client with no idea what the monster in front of it is doing. It
 *     is the same argument `PlayerState.dead` already won: a client that joined
 *     after the event has no patch history to infer anything from, and a state
 *     is reconciled where an event list is replayed.
 *
 * The row also warns that answering one of *"what is this body doing"* in
 * isolation is how a room ends up with three channels for one question. This is
 * the one channel: a blocked walk (`TODO.md` #140) and an arrival (#100) are
 * readings of this same field the day they are derivable, not fields of their
 * own.
 *
 * ## Every code is derived from a mechanic that already exists
 *
 * Nothing here is a timer this package invented, and nothing here is a duration
 * this package restates. `walking` is the walker, `dead` is the death registry,
 * `striking` is `attackCooldownTicks` — core's own count of *"how many
 * simulation ticks one swing of this profile costs"* — `casting` is
 * `skillCooldownTicks`, and `hurt` and `blocking` are the tick a swing actually
 * landed on the body, read off `AttackResult` and `VitalsChange` where the room
 * resolves them. `apps/game`'s `world/actions.ts` is where that reading happens,
 * and it reads core rather than repeating it.
 *
 * **`winding-up` was absent for exactly that reason, and is here now because the
 * reason went away.** The old text read: *"a code for one is exactly what the art
 * wants and exactly what no rule can produce — a monster's blow lands on the
 * first tick of its swing and a character's on the next step after the one it was
 * admitted on, and no number anywhere in `@d2/core` says how long an anticipation
 * would be"*. Core now carries D2's **ActionFlag** — the frame of an attack
 * animation on which the blow actually lands — as `AttackProfile.hitFrame`,
 * converts it onto the tick grid as `attackWindUpTicks`, and the tick *schedules
 * the damage there* (`TODO.md` #483). So the eighth code is a timed server-side
 * state read off the same number the damage is, rather than a pose invented for
 * the art: the telegraph a client draws ends on the tick the blow lands, because
 * both are one number. What is still absent is the cast half — `skillCooldownTicks`
 * is D2's casting *delay* and a cast animation with an ActionFlag of its own is a
 * second number core does not carry — and it is absent on the old grounds.
 *
 * ## The two agreements
 *
 * `walking` and `dead` stay exactly as they were: they are mechanical facts
 * other code depends on — the interpolator reads one, the respawn countdown the
 * other — and this field is the *visual* reading of the same truth. Two readings
 * of one truth can disagree, and a client cannot tell which of them is wrong; it
 * would simply draw a corpse swinging. So the disagreement is made
 * unexpressible rather than merely discouraged:
 *
 *   - **`action === 'dead'` if and only if `dead`.** The only writer of the dead
 *     code is `markDead`, which writes the flag in the same breath, and
 *     `markAlive` clears both. `setPlayerAction` refuses the code outright, and
 *     refuses *every* action while the seat is dead — which is precisely "a
 *     corpse still reports striking", refused at the wire rather than caught in
 *     review.
 *   - **`action === 'walking'` only when `walking`.** One direction, not both: a
 *     body walking towards something it is also mid-swing at is walking *and*
 *     striking, and the field says the more specific of the two. So the
 *     agreement is that the visual reading never claims a walk the mechanical
 *     fact denies.
 *
 * A disagreement is malformed construction rather than an ordinary outcome, so
 * it throws — the same line `markDead`, `setPlayerLife` and `describeLevel`
 * already draw. It is the server's own state and the server is holding it
 * wrongly.
 *
 * ## What it costs
 *
 * Two bytes per transition and **nothing per tick**, which is the whole reason
 * it can be afforded on sixty bodies. The field is written every tick, like the
 * position beside it, and a value equal to the one a field already holds is not
 * a change — so a monster that is striking goes on striking for the whole of its
 * animation for free, and the expensive tick (sixty bodies chasing at once, 725
 * bytes) is exactly the tick on which every one of them is `walking` and
 * unchanged. What it *can* cost is bounded by the cadence rather than by the
 * population: a body's action only moves when a swing starts, lands, or ends,
 * and `bandwidth.test.ts` measures a whole floor in melee at a few bytes a tick
 * against the 768 the chase already spends.
 */
/**
 * The states a body can be replicated in, in the order their codes are
 * assigned.
 *
 * **Append only.** A code is a wire value: reordering this list re-labels every
 * body already deployed, and a client one release behind would draw last
 * month's code with this month's meaning — silently, and for ever. It is the
 * same rule, and the same reason, as `MonsterState`'s field order and
 * `ELITE_AFFIX_IDS`' bit order.
 *
 * Each one and where it comes from:
 *
 *   | code       | derived from                                                |
 *   |------------|-------------------------------------------------------------|
 *   | `idle`     | none of the below — the state a body is in by default        |
 *   | `walking`  | `Entity.walker`, the same fact `walking` already reports     |
 *   | `striking` | the swing cadence: `readyToSwing`/`attackCooldownTicks` for a character, core's own `MonsterBrain.recovery` for a monster |
 *   | `blocking` | `AttackResult.outcome === 'blocked'` on the tick it was rolled |
 *   | `hurt`     | `VitalsChange.lifeLost > 0` on the tick the blow landed      |
 *   | `casting`  | the casting delay: `readyToCast`/`skillCooldownTicks`        |
 *   | `dead`     | `PlayerState.dead`, and nothing else                         |
 *   | `winding-up` | the part of a strike before the blow: core's `windingUp` against `attackWindUpTicks` for a character and `MonsterBrain.windUpTicks` for a monster |
 *
 * `winding-up` is **appended and not slotted in beside `striking`**, however much
 * it belongs there by meaning. The two are one animation read either side of the
 * hit frame and a reader would like them adjacent; a wire value is a number, and
 * putting it at index 3 would leave `blocking`, `hurt`, `casting` and `dead`
 * naming what the four codes after them used to — every one of them a state a
 * deployed client would go on drawing, wrongly, with no unknown code to give it
 * away. `actions.test.ts` pins the deployed numbers, not merely the order.
 */
export const BODY_ACTIONS = Object.freeze([
    'idle',
    'walking',
    'striking',
    'blocking',
    'hurt',
    'casting',
    'dead',
    'winding-up',
]);
/**
 * The code a body carries before anybody has said anything about it: `idle`.
 *
 * Zero-based, unlike `MonsterState.archetype`, and the difference is the point.
 * An archetype reserves zero for *"nobody wrote this"* because a monster that
 * reached a client with its archetype unset would otherwise render as a melee
 * one and hide the omission for ever. An action has no such hole: `idle` is a
 * state a body is genuinely in, it is exactly what the two booleans beside it
 * already default to — not walking, not dead — and a body that nothing has
 * described yet is standing there doing nothing, which is what a client would
 * draw anyway.
 *
 * So zero is the honest default *and* the encoder's cheapest value: sixty
 * monsters spawn idle and pay nothing at all to say so, the same bargain
 * `lifeBand` strikes by being full from construction.
 */
export const IDLE_ACTION = 0;
/** The code for an action. Throws on one this wire cannot name — see `BODY_ACTIONS`. */
export const actionCode = (action) => {
    const index = BODY_ACTIONS.indexOf(action);
    if (index < 0)
        throw new RangeError(`the wire cannot name the action "${action}"`);
    return index;
};
/**
 * The action a code names.
 *
 * The client's half of the contract, and it refuses an unknown code rather than
 * guessing — the same line `monsterArchetypeOf` draws, and for a sharper reason:
 * the fallback anybody would reach for is `idle`, which is *"draw the swinging
 * monster standing still"*, which is the exact bug this field exists to fix. A
 * code no release of this list has ever held is a server the client cannot
 * follow, and hiding that behind a plausible-looking animation would hide it for
 * ever.
 */
export const bodyActionOf = (code) => {
    const action = BODY_ACTIONS[code];
    if (action === undefined)
        throw new RangeError(`no action has the code ${code}`);
    return action;
};
/** The code a dead seat carries. Written by `markDead` and by nothing else. */
export const DEAD_ACTION = actionCode('dead');
/** The code a walking body carries, which is only ever written beside a `walking` of `true`. */
const WALKING_ACTION = actionCode('walking');
/**
 * Records what a seat is doing.
 *
 * The single writer of `PlayerState.action` for everything except the death,
 * and it holds both agreements at the moment the field is written rather than
 * leaving them to a caller's discipline:
 *
 *   - **A dead seat takes no action at all.** Not `striking`, not `idle`, not
 *     even `dead` — the death is `markDead`'s to write, together with the flag
 *     and the countdown it belongs to, exactly as that function already writes
 *     three fields for one fact. A second way onto the wire as dead would be a
 *     second way for the two to part company.
 *   - **`walking` is refused unless the seat is walking.** The mechanical fact
 *     is the truth and this is its reading; a reading that claimed a walk the
 *     fact denies would be interpolated towards a destination nothing is moving
 *     to.
 *
 * Both refusals are malformed construction and throw (`CLAUDE.md`): it is the
 * server's own state, and a client cannot be told about a body the server is
 * describing two ways at once.
 */
export const setPlayerAction = (player, action) => {
    if (player.dead) {
        throw new RangeError(`a dead seat is doing nothing, and cannot be described as "${action}"`);
    }
    if (action === 'dead') {
        throw new RangeError('a seat is put on the wire as dead by `markDead`, which writes the flag with it');
    }
    if (action === 'walking' && !player.walking) {
        throw new RangeError('a seat that is not walking cannot be described as walking');
    }
    player.action = actionCode(action);
};
/**
 * Records what a monster is doing.
 *
 * The same walking agreement, and one refusal a seat does not have: **a monster
 * is never on this wire as dead.** A monster's death is a removal — the room
 * deletes the map entry on the tick the body is reaped (`bury`) — where a
 * player's is a seat waiting for its body back. A monster replicated as dead
 * would therefore be a corpse no despawn ever takes away, which is a state the
 * simulation cannot produce and the wire should not be able to describe.
 */
export const setMonsterAction = (monster, action) => {
    if (action === 'dead') {
        throw new RangeError('a monster leaves this wire when it dies rather than being described as dead');
    }
    if (action === 'walking' && !monster.walking) {
        throw new RangeError('a monster that is not walking cannot be described as walking');
    }
    monster.action = actionCode(action);
};
/**
 * Takes the walking action back off a body whose walk has just ended.
 *
 * Called by `clearDestination`, so the flag and its reading end together. It
 * exists because the room is not the only thing that ends a walk: a descent
 * reseats every player mid-message (`reseatPlayer`), and a patch can be encoded
 * before the next tick rewrites the action — which would put a standing body on
 * the wire still described as walking.
 *
 * Only the walking action is touched. A body that was striking is still
 * striking when its feet stop, and clearing that would be this package deciding
 * a rule.
 */
export const endWalkingAction = (player) => {
    if (player.action === WALKING_ACTION)
        player.action = IDLE_ACTION;
};
//# sourceMappingURL=actions.js.map